import { useCallback, RefObject } from "react";

interface UseScrollToErrorOptions {
  /**
   * Selector to find the input element inside the container
   * @default 'input[type="text"]'
   */
  inputSelector?: string;
  /**
   * Delay in milliseconds before focusing the input
   * @default 100
   */
  delay?: number;
  /**
   * Scroll behavior options
   * @default { behavior: "smooth", block: "center" }
   */
  scrollOptions?: ScrollIntoViewOptions;
}

/**
 * Custom hook to scroll to an element and focus on an input inside it
 * @param containerRef - Ref to the container element
 * @param options - Configuration options
 * @returns Function to scroll and focus
 */
export default function useScrollToError(
  containerRef: RefObject<HTMLElement>,
  options?: UseScrollToErrorOptions,
) {
  const {
    inputSelector = 'input[type="text"]',
    delay = 100,
    scrollOptions = { behavior: "smooth", block: "center" },
  } = options || {};

  const scrollAndFocus = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    // Scroll to container
    containerRef.current.scrollIntoView(scrollOptions);

    // Find and focus the input element after delay
    setTimeout(() => {
      const inputElement = containerRef.current?.querySelector<HTMLInputElement>(inputSelector);
      if (inputElement) {
        inputElement.focus();
      }
    }, delay);
  }, [containerRef, inputSelector, delay, scrollOptions]);

  return scrollAndFocus;
}

